<template>
  <div class="plan-box">
    <slot>
      <div class="plan-box_header">
        <div class="plan-box_header_title">{{ title }}</div>
        <div class="plan-box_header_subtitle">{{ subtitle }}</div>

        <div
          v-if="startingAtPrice"
          class="starting-at"
        >
          <span v-if="plan.affiliate">
            {{ $t('subscription.starting_at') }}
            <span class="weight_500 text-crossed-out">
              {{ numFixed(plan.normalPrice, 0) }} USD
            </span>
          </span>
          <span v-else>
            {{ $t('subscription.starting_at') }}
          </span>
          <div class="starting-at-title">
            <div :class="{ 'affiliate-price': plan.affiliate }">
              {{ numFixed(plan.startingAt, 0) }}
              <span class="starting-at-month"> <b>USD</b> / {{ $t('subscription.month') }} </span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isCurrentPlan && !managePlan"
        class="plan-box_btn cursor-not-allowed"
      >
        <v-btn
          size="x-large"
          class="w-100 no-events text-wrap"
          color="#9e9e9e"
          >{{ btnText }}</v-btn
        >
      </div>

      <v-btn
        v-else
        data-tst="btn_plan_box"
        class="plan-box_btn text-wrap"
        :color="managePlan ? 'var(--clr-black-1)' : 'var(--clr-primary)'"
        :variant="managePlan ? 'outlined' : 'flat'"
        :style="`color:${managePlan ? 'var(--clr-black-1)' : 'var(--clr-white-0)'}`"
        size="x-large"
        @click="handleClick(id)"
      >
        {{ btnText }}
      </v-btn>

      <div
        v-if="features"
        class="plan-box_features"
      >
        <div
          v-for="feature in features"
          :key="feature.text"
          class="plan-box_features_feature"
          :style="feature.css_style || ''"
        >
          <v-icon
            color="primary"
            class="mr-2"
            >mdi-check</v-icon
          >
          <component
            :is="feature.tooltip ? 'TooltipUnderline' : 'span'"
            v-bind="
              feature.tooltip
                ? {
                    img: feature.tooltip.img,
                    text: feature.tooltip.text,
                    hasHelp: feature.tooltip.hasHelp && !onceHovered,
                  }
                : {}
            "
          >
            <!-- <div class="d-flex"> -->
            <span v-html="feature.text"></span>
            <span
              v-if="feature.hasTagSoon"
              class="tag-soon"
            >
              {{ feature.tagText ? feature.tagText : $t('common.soon') }}
            </span>
            <!-- </div> -->
          </component>
        </div>
      </div>
    </slot>

    <slot :name="hasAiBtnSlot ? 'ai_btn' : 'none'"></slot>
  </div>
</template>

<script>
import { gtm_sub_selection } from '@/helpers/gtm'
import { seg_track, seg_getProduct, seg_getTotalAndCurrency } from '@/helpers/segment'

import TooltipUnderline from '@/components/Payments/TooltipUnderline'
import format_mix from '@/mixins/format_mix'

export default {
  name: 'PlanBox',
  components: {
    TooltipUnderline,
  },
  mixins: [format_mix],
  props: {
    plan: Object,
    startingAtPrice: [Boolean, Number],
    title: String,
    subtitle: String,
    features: Array,
    id: String,
    btnText: String,
    isCurrentPlan: Boolean,
    managePlan: Boolean,
    onceHovered: Boolean,
    customGame: String,
    hasAiBtnSlot: Boolean,
  },
  data() {
    return {
      error: false,
    }
  },
  methods: {
    handleClick() {
      if (this.managePlan) {
        this.$router.push({
          name: this.$store.getters.user_has_flag('REACT_SUBSCRIPTION_OVERVIEW')
            ? 'UserSubscriptionsOverview'
            : 'UserSubscription',
          query: {},
        })
      } else {
        gtm_sub_selection({
          currencyCode: 'USD',
          game: this.customGame,
          type: this.id,
          from_path_name: this.$store.state.router.from_path_name || this.$route.name,
          // price: this.plan.startingAt,
        })
        seg_track('subscription_choosen', {
          affiliation: this.$store.getters.user_info.affiliate_name,
          ...seg_getTotalAndCurrency([this.plan.seg_plan]),
          products: [
            {
              ...seg_getProduct(this.plan.seg_plan),
              // quantity: 1,
            },
          ],
        })
        this.$router.push({
          name: 'SubscriptionPaymentSelection',
          query: {
            gm_type: this.$route.query.gm_type || 'cash',
            gm_tier: this.id,
          },
        })
      }
    },
  },
  computed: {
    mdAndDown() {
      const { globals_mobile } = this.$store.getters
      return globals_mobile
    },
    smallScreen() {
      const { globals_window_size } = this.$store.getters
      return globals_window_size.width < 768
    },
  },
}
</script>

<style lang="scss" scoped>
.b-dark {
  background: var(--clr-black-0);
  color: w;
}
.b-light {
  background: var(--clr-white-0);
  color: var(--clr-black-1);
}
.p-slim {
  padding: 1rem 1.5rem !important;
}

.b-rounded {
  border-radius: 4px !important;
}
.b-box-dark {
  position: relative;
  margin-right: 1rem;
  border-radius: 4px !important;
  background: rgba(0, 0, 0, 0.33);
}

.affiliate-price {
  position: relative;
  z-index: 1;
  &:after {
    content: '';
    background: var(--clr-yellow);
    filter: brightness(1.1);
    position: absolute;
    z-index: -1;
    height: 100%;
    width: 200%;
    left: -50%;
    top: 0;
  }
}

.tag-soon {
  white-space: nowrap;
  position: relative;
  line-height: 0.7rem;
  padding: 0.0125rem 0.25rem;
  font-size: 0.6rem;
  background: var(--clr-grey-2);
  border-radius: 4px;
  color: black;
  text-transform: uppercase;
  margin-left: 0.25rem;
}

.starting-at {
  display: flex;
  flex-direction: column;
  margin: auto 0 0 0;
  position: relative;
  .starting-at-title {
    font-size: 1.5rem;
    font-weight: 700;
    .starting-at-month {
      font-size: 1rem;
      font-weight: 400;
    }
  }
  .text-early-bird {
    position: relative;
    font-size: 0.8rem;
    color: var(--clr-blue-1);
    font-weight: 700;
  }
}

.plan-box {
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  --header-flex: 0;
  &_header {
    margin-bottom: 0.5rem;
    flex: var(--header-flex);
    display: flex;
    flex-direction: column;
    &_title {
      font-size: 1.5rem;
      font-weight: 700;
    }
    &_subtitle {
      margin-bottom: 1rem;
    }
  }
  &_features {
    margin-top: 1rem;
    margin-bottom: auto;
    &_feature {
      line-height: 1.8;
      display: flex;
      align-items: center;
    }
  }
}
.no-bold {
  .plan-box_features_feature {
    font-weight: 400 !important;
  }
}
</style>
